import React, { useState, useCallback, useMemo } from 'react';
import { AppRenderer } from '@mcp-ui/client';
import type { MCPUIResource } from '@/types/chat';
import { buildFetchConfig } from '@/utils/fetchUtils';
import { Maximize2, Minimize2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  ADP_EVENTS,
  getBernoulliAdp,
  trackAdpEvent,
  trackAdpWaypoint,
} from '@/services/analytics/adp';

interface UIResourceBlockProps {
  resources: MCPUIResource[];
  onAction?: (action: string, data: unknown) => Promise<void>;
}

const SANDBOX_URL = new URL('/sandbox_proxy.html', window.location.origin);

// Apps report their content height through ui/notifications/size-changed.
// A block starts at the default and follows what the app reports, never
// exceeding the default, so full-height templates keep their size and scrolling.
const DEFAULT_FRAME_HEIGHT = 600;
const MIN_FRAME_HEIGHT = 80;

export const UIResourceBlock: React.FC<UIResourceBlockProps> = ({
  resources,
  onAction,
}) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [frameHeights, setFrameHeights] = useState<Record<number, number>>({});

  const handleSizeChanged = useCallback(
    (idx: number) => (params: { width?: number; height?: number }) => {
      const reported = params.height;
      if (
        typeof reported !== 'number' ||
        !Number.isFinite(reported) ||
        reported <= 0
      ) {
        return;
      }
      const next = Math.min(
        DEFAULT_FRAME_HEIGHT,
        Math.max(MIN_FRAME_HEIGHT, Math.ceil(reported)),
      );
      setFrameHeights(prev =>
        prev[idx] === next ? prev : { ...prev, [idx]: next },
      );
    },
    [],
  );

  const sandboxConfig = useMemo(() => ({ url: SANDBOX_URL }), []);

  const handleToolCall = useCallback(
    (resourceUri?: string) =>
      async (params: { name: string; arguments?: Record<string, unknown> }) => {
        const args = params.arguments ?? {};

        const { baseURL, headers } = buildFetchConfig();
        try {
          const res = await fetch(`${baseURL}/mcp/tools/call`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              name: params.name,
              arguments: args,
              resource_uri: resourceUri,
            }),
          });
          if (!res.ok) {
            const detail = await res.text();
            return {
              content: [{ type: 'text' as const, text: detail }],
              isError: true,
            };
          }
          return await res.json();
        } catch (err) {
          console.error('[MCP_TOOL_ERROR]', {
            tool: params.name,
            error: String(err),
            service_id: 'app-water-ai-bernoulli',
          });
          return {
            content: [
              { type: 'text' as const, text: `Tool call failed: ${err}` },
            ],
            isError: true,
          };
        }
      },
    [],
  );

  const handleMessage = useCallback(
    async (params: {
      role: string;
      content: Array<{ type: string; text?: string }>;
    }) => {
      if (!onAction || !params.content) return {};

      const text = params.content
        .filter(block => block.type === 'text' && block.text)
        .map(block => block.text)
        .join('\n');

      if (text) {
        trackAdpWaypoint(
          getBernoulliAdp(),
          ADP_EVENTS.UI_RESOURCE_PROMPT_SUBMIT,
          {},
        );
        // The host attaches any model context this view has published.
        await onAction('ui-prompt', { prompt: text });
      }
      return {};
    },
    [onAction],
  );

  const handleFallbackRequest = useCallback(
    (resourceUri?: string) =>
      async (request: { method: string; params?: Record<string, unknown> }) => {
        if (request.method === 'ui/update-model-context') {
          const params = request.params as {
            content?: Array<{ type: string; text?: string }>;
          };
          const context = (params?.content ?? [])
            .filter(block => block.type === 'text' && block.text)
            .map(block => block.text)
            .join('\n');
          // Publish to the host so the next user message carries it, whether
          // that message comes from this view or from the chat composer.
          await onAction?.('ui-model-context', { resourceUri, context });
          return {};
        }
        throw new Error(`Unsupported method: ${request.method}`);
      },
    [onAction],
  );

  const handleOpenLink = useCallback(async (params: { url: string }) => {
    if (params.url.startsWith('https://') || params.url.startsWith('http://')) {
      trackAdpEvent(getBernoulliAdp(), ADP_EVENTS.UI_RESOURCE_OPEN_LINK, {
        url: params.url,
      });
      window.open(params.url, '_blank', 'noopener,noreferrer');
    }
    return {};
  }, []);

  const handleReadResource = useCallback(async (params: { uri: string }) => {
    const { baseURL, headers } = buildFetchConfig();
    const res = await fetch(
      `${baseURL}/mcp/resources/read?uri=${encodeURIComponent(params.uri)}`,
      { headers },
    );
    if (!res.ok) {
      console.error('[MCP_RESOURCE_ERROR]', {
        uri: params.uri,
        status: res.status,
        service_id: 'app-water-ai-bernoulli',
      });
      throw new Error(`Failed to read resource: ${res.statusText}`);
    }
    return res.json();
  }, []);

  const closeExpanded = useCallback(() => {
    trackAdpEvent(getBernoulliAdp(), ADP_EVENTS.UI_RESOURCE_BACKDROP_CLOSE, {});
    setExpandedIndex(null);
  }, []);

  if (!resources || resources.length === 0) {
    return null;
  }

  return (
    <div className="w-full space-y-4">
      {resources.map((resource, idx) => {
        const isExpanded = expandedIndex === idx;
        const toolName =
          resource.toolName ??
          resource.uri?.replace('ui://', '').split('/')[1] ??
          'mcp-tool';

        return (
          <div
            key={`ui-resource-${idx}`}
            className={cn(
              'rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm transition-all duration-200',
              isExpanded &&
                'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] shadow-2xl w-[85vw] max-w-6xl h-[85vh]',
            )}>
            <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-700">
                  Visualization
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => {
                    const next = isExpanded ? null : idx;
                    trackAdpEvent(
                      getBernoulliAdp(),
                      ADP_EVENTS.UI_RESOURCE_EXPAND_TOGGLE,
                      {
                        state: next !== null ? 'expanded' : 'collapsed',
                        tool_name: toolName,
                      },
                    );
                    setExpandedIndex(next);
                  }}
                  className="p-1.5 rounded-md hover:bg-slate-200 transition-colors"
                  title={isExpanded ? 'Minimize' : 'Expand'}>
                  {isExpanded ? (
                    <Minimize2 className="w-4 h-4 text-slate-600" />
                  ) : (
                    <Maximize2 className="w-4 h-4 text-slate-600" />
                  )}
                </button>
              </div>
            </div>

            <div
              className={cn(
                'w-full [&_iframe]:!w-full [&_iframe]:!h-full',
                isExpanded && 'h-[calc(100%-48px)]',
              )}
              style={
                isExpanded
                  ? undefined
                  : { height: frameHeights[idx] ?? DEFAULT_FRAME_HEIGHT }
              }>
              <AppRenderer
                toolName={toolName}
                sandbox={sandboxConfig}
                toolResourceUri={resource.resourceUri}
                toolInput={resource.toolInput}
                toolResult={
                  resource.toolResult
                    ? {
                        content: [
                          {
                            type: 'text' as const,
                            text:
                              typeof resource.toolResult === 'string'
                                ? resource.toolResult
                                : JSON.stringify(resource.toolResult),
                          },
                        ],
                        isError: false,
                      }
                    : undefined
                }
                onReadResource={handleReadResource}
                onCallTool={handleToolCall(resource.resourceUri)}
                onMessage={handleMessage}
                onOpenLink={handleOpenLink}
                onSizeChanged={handleSizeChanged(idx)}
                onFallbackRequest={handleFallbackRequest(resource.resourceUri)}
                onError={err =>
                  console.error('[MCP-UI] AppRenderer error:', err)
                }
              />
            </div>
          </div>
        );
      })}

      {expandedIndex !== null && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Close expanded view"
          className="fixed inset-0 bg-black/60 z-50 cursor-pointer"
          onClick={closeExpanded}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === 'Escape') {
              closeExpanded();
            }
          }}
        />
      )}
    </div>
  );
};
